import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ApplicationCard } from './ApplicationCard'

interface Application {
  id: string
  stageId: string
  candidate: {
    id: string
    name: string
    email: string
    avatarUrl?: string
    rating?: number
  }
  appliedAt: string
  source?: string
  hasResume?: boolean
  interviewCount?: number
}

interface Stage {
  id: string
  name: string
  color: string
}

interface PipelineColumnProps {
  stage: Stage
  applications: Application[]
  isDropTarget?: boolean
  onApplicationClick?: (application: Application) => void
  onDrop?: (applicationId: string, stageId: string) => void
}

export function PipelineColumn({
  stage,
  applications,
  isDropTarget,
  onApplicationClick,
  onDrop,
}: PipelineColumnProps) {
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const applicationId = e.dataTransfer.getData('applicationId')
    if (applicationId && onDrop) {
      onDrop(applicationId, stage.id)
    }
  }

  return (
    <div
      className={`
        flex-shrink-0 w-72 flex flex-col bg-muted/30 rounded-lg border
        transition-all duration-200
        ${isDragOver ? 'border-primary bg-primary/5' : 'border-transparent'}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: stage.color }}
            />
            <h3 className="font-medium text-sm">{stage.name}</h3>
            <Badge variant="secondary" className="text-xs">
              {applications.length}
            </Badge>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Stage options">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Applications */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px] max-h-[calc(100vh-280px)]">
        <AnimatePresence mode="popLayout">
          {applications.map((application) => (
            <motion.div
              key={application.id}
              layout
              draggable
              onDragStart={(e: any) => {
                e.dataTransfer.setData('applicationId', application.id)
              }}
              className="group"
            >
              <ApplicationCard
                application={application}
                onClick={() => onApplicationClick?.(application)}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {applications.length === 0 && (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            No applications
          </div>
        )}
      </div>

      {/* Drop zone indicator */}
      {isDragOver && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 48 }}
          exit={{ opacity: 0, height: 0 }}
          className="mx-2 mb-2 border-2 border-dashed border-primary rounded-lg flex items-center justify-center"
        >
          <span className="text-xs text-primary font-medium">Drop here</span>
        </motion.div>
      )}
    </div>
  )
}
